/**
 * Wizard steps 2–4.
 *
 * These render the SAME components the standalone tabs render — the leads table
 * and its two dialogs from `@/components/leads`, the template viewer, picker,
 * split editor and import dialog from `@/components/template`. The wizard
 * sequences existing screens; it does not own a second version of any of them.
 */
import React from 'react'
import { Button, Field, Input, Radio, Switch } from '@/components/ds'
import { Ic } from '@/components/icons'
import { Avatar, QueryState } from '@/components/common'
import {
  AddLeadDialog,
  DownloadCsvTemplateButton,
  ImportLeadsDialog,
  LeadsTable,
} from '@/components/leads'
import {
  ImportTemplateDialog,
  TemplateList,
  TemplatePickerDialog,
  TemplateModeToggle,
  TemplatePreviewDialog,
  TemplateSplitEditor,
  TemplateViewer,
  createBlankTemplate,
  mergeFieldsFromLeads,
  previewLead,
  senderPreview,
  synthesizeSampleLead,
  useRenderedTemplate,
  useTemplateDraft,
} from '@/components/template'
import { createTemplate } from '@/lib/db'
import { useToast } from '@/lib/toast'
import { describeGaps, type LeadFieldGap } from '@shared/gaps'
import { htmlToText } from '@shared/render'
import { describeDelay } from '@shared/schedule'
import type { CampaignDraft } from '@/lib/db'
import type { Lead, SmtpAccountSafe, Template } from '@shared/types'

/** How tall the viewer / editor stand inside the wizard's scrolling step body. */
const PANEL_HEIGHT = 'clamp(380px, 58vh, 660px)'

function SectionTitle({ children, hint }: { children: React.ReactNode; hint?: string }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 15.5, fontWeight: 600, color: 'var(--text-primary)' }}>{children}</div>
      {hint && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>{hint}</div>}
    </div>
  )
}

// --- step 2: leads ------------------------------------------------------------

export function LeadsStep({
  campaign,
  rows,
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  loading,
  error,
  expected,
  gaps,
  onChanged,
  onRetry,
}: {
  campaign: { id: string; name: string }
  rows: Lead[]
  total: number
  page: number
  pageSize: number
  onPageChange: (p: number) => void
  onPageSizeChange: (n: number) => void
  loading: boolean
  error: string | null
  /** Merge fields the bound template expects, shown as import guidance. */
  expected: string[]
  /** Template tokens the current leads can't fill — the reconciliation panel. */
  gaps: LeadFieldGap[]
  onChanged: () => void
  onRetry: () => void
}) {
  const toast = useToast()
  const [adding, setAdding] = React.useState(false)
  const [importing, setImporting] = React.useState(false)

  // One option, so both dialogs are locked to the campaign being built.
  const campaignOptions = [{ value: campaign.id, label: campaign.name || 'This campaign' }]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <SectionTitle hint="Added straight to this campaign — the same table and importer as the Leads tab.">
        Who receives it
      </SectionTitle>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {expected.length > 0 && (
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Your template merges: {expected.map((f) => `{{${f}}}`).join(', ')}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <DownloadCsvTemplateButton tokens={expected} />
        <Button
          size="sm"
          variant="secondary"
          icon={<Ic.upload size={14} />}
          onClick={() => setImporting(true)}
        >
          Import CSV
        </Button>
        <Button size="sm" icon={<Ic.plus size={14} />} onClick={() => setAdding(true)}>
          Add lead
        </Button>
      </div>

      {/* THE RECONCILIATION PANEL: leads the bound template cannot render.
          The Continue gate refuses on the same server check, so this is the
          explanation, not the enforcement. */}
      {gaps.length > 0 && (
        <div
          role="alert"
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
            padding: '9px 12px',
            borderRadius: 8,
            background: 'var(--danger-soft, var(--status-failed-bg))',
            color: 'var(--danger-text)',
            fontSize: 12.5,
          }}
        >
          <span style={{ flex: 'none', marginTop: 1, display: 'inline-flex' }}>
            <Ic.warning size={14} />
          </span>
          <span>
            <strong>{describeGaps(gaps)}.</strong> Those sends would fail — fill the values in
            (remove the lead and re-add it, or re-import a CSV that has the column), or edit the
            template to drop the token. A dash in the table marks each empty value.
          </span>
        </div>
      )}

      <QueryState
        loading={loading}
        error={error}
        empty={rows.length === 0}
        emptyTitle="No leads yet"
        emptyDescription="Add one by hand or import a CSV with email, name, and any merge fields. At least one lead is required before you can continue."
        emptyAction={
          <Button icon={<Ic.plus size={14} />} onClick={() => setAdding(true)}>
            Add lead
          </Button>
        }
        onRetry={onRetry}
      >
        <LeadsTable
          rows={rows}
          total={total}
          campaign={campaign}
          page={page}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          onChanged={onChanged}
          bodyMaxHeight={330}
        />
      </QueryState>

      {adding && (
        <AddLeadDialog
          campaigns={campaignOptions}
          defaultCampaignId={campaign.id}
          onClose={() => setAdding(false)}
          onAdded={(message) => {
            setAdding(false)
            toast('success', 'Lead added', message)
            onChanged()
          }}
        />
      )}

      {importing && (
        <ImportLeadsDialog
          campaigns={campaignOptions}
          defaultCampaignId={campaign.id}
          onClose={() => setImporting(false)}
          onDone={(inserted, queued, skipped) => {
            setImporting(false)
            toast(
              'success',
              `${inserted} lead${inserted === 1 ? '' : 's'} imported`,
              `${queued} queued${skipped ? ` · ${skipped} skipped` : ''}`,
            )
            onChanged()
          }}
        />
      )}
    </div>
  )
}

// --- step 3: template ---------------------------------------------------------

export function TemplateStep({
  campaignId,
  campaign,
  identities,
  templates,
  boundTemplateId,
  onBind,
  loading,
  error,
  leadRows,
  narrow,
  onChanged,
  onRetry,
}: {
  campaignId: string
  /** The draft being built — drives the from / reply-to shown in the viewer. */
  campaign: Pick<CampaignDraft, 'smtp_account_id' | 'from_name' | 'reply_to'>
  identities: SmtpAccountSafe[]
  templates: Template[]
  /** SERVER-confirmed binding: what campaigns.template_id actually points at. */
  boundTemplateId: string | null
  /** Binds a template to the campaign and confirms it stuck. Throws on failure. */
  onBind: (templateId: string) => Promise<void>
  loading: boolean
  error: string | null
  leadRows: Lead[]
  narrow?: boolean
  onChanged: () => void
  onRetry: () => void
}) {
  const toast = useToast()
  const [selectedId, setSelectedId] = React.useState<string | null>(null)
  const [lastOpened, setLastOpened] = React.useState<Template | null>(null)
  const [editing, setEditing] = React.useState(false)
  const [importing, setImporting] = React.useState(false)
  const [choosing, setChoosing] = React.useState(false)
  const [showPreview, setShowPreview] = React.useState(false)
  const [busy, setBusy] = React.useState(false)

  // What the sender will actually use — read back from campaigns.template_id,
  // never guessed from the list order.
  const bound = templates.find((t) => t.id === boundTemplateId) ?? null

  // Templates this campaign created, plus whatever it is bound to. The rest of
  // the library stays behind the picker so the step does not turn into a list.
  const ownTemplates = templates.filter(
    (t) => t.campaign_id === campaignId || t.id === boundTemplateId,
  )

  // The template just opened (or created) wins until the list reload catches
  // up, so the editor never snaps back to an older one mid-edit.
  const selected =
    templates.find((t) => t.id === selectedId) ??
    (lastOpened && lastOpened.id === selectedId ? lastOpened : null) ??
    bound ??
    ownTemplates[0] ??
    null

  const editor = useTemplateDraft(selected)
  const draft = editor.draft

  // With no leads yet (the normal case now that Template comes first) the
  // preview renders as a synthesized sample that carries a stand-in value for
  // every custom token the CURRENT draft merges — so typing {{role}} shows
  // "Role" instead of a false "unresolved" flag. A real lead is never
  // synthesized over: a genuine gap must keep showing.
  const sampleLead = leadRows.length
    ? previewLead(leadRows)
    : synthesizeSampleLead(draft ?? selected)
  const mergeFields = mergeFieldsFromLeads(leadRows)
  const sender = senderPreview({ ...campaign }, identities)
  const rendered = useRenderedTemplate(draft, sampleLead, 0)
  const canSendTest = identities.some((i) => i.has_password)

  function open(t: Template, forEditing: boolean) {
    setLastOpened(t)
    setSelectedId(t.id)
    editor.select(t)
    setEditing(forEditing)
  }

  /**
   * Create/import/pick all funnel through here: a template is only "added" once
   * the campaign points at it on the server. Without this the campaign kept a
   * template it never referenced, and every send failed with "no template".
   */
  async function adopt(template: Template, forEditing: boolean) {
    await onBind(template.id)
    open(template, forEditing)
    onChanged()
  }

  async function writeOne() {
    setBusy(true)
    try {
      // Standalone (campaign_id null): it joins the library like any other,
      // and the binding below is what ties it to this campaign.
      await adopt(await createBlankTemplate(null), true)
    } catch (e) {
      toast('error', 'Could not create', e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  /** Pasted markup is stored VERBATIM as an html-mode template, then opened. */
  async function importTemplate(input: { name: string; subject: string; html: string }) {
    setBusy(true)
    try {
      const created = await createTemplate({
        campaign_id: null,
        name: input.name.trim(),
        subject: input.subject.trim(),
        body_html: input.html,
        body_text: htmlToText(input.html),
        body_mode: 'html',
      })
      setImporting(false)
      await adopt(created, true)
      toast('success', `${input.name.trim()} imported`, 'Bound to this campaign and saved to your library')
    } catch (e) {
      toast('error', 'Import failed', e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function save() {
    try {
      const saved = await editor.save()
      if (saved) {
        setLastOpened(saved)
        // Editing the template the campaign is not yet bound to would leave the
        // campaign unsendable, so saving also confirms the binding.
        if (saved.id !== boundTemplateId) await onBind(saved.id)
      }
      setEditing(false)
      toast('success', 'Template saved', 'Bound to this campaign and live on the Templates tab')
      onChanged()
    } catch (e) {
      toast('error', 'Save failed', e instanceof Error ? e.message : String(e))
    }
  }

  /** Make the picked template the one this campaign actually sends. */
  async function use(t: Template) {
    setBusy(true)
    try {
      await adopt(t, false)
      setChoosing(false)
      toast('success', 'Template bound', 'Emails already queued for this campaign use it too')
    } catch (e) {
      toast('error', 'Could not bind', e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const dialogs = (
    <>
      {choosing && (
        <TemplatePickerDialog
          templates={templates}
          loading={loading}
          boundTemplateId={boundTemplateId}
          sampleLead={sampleLead}
          busy={busy}
          onClose={() => setChoosing(false)}
          onChoose={use}
        />
      )}
      {importing && (
        <ImportTemplateDialog
          busy={busy}
          onClose={() => setImporting(false)}
          onImport={importTemplate}
        />
      )}
      {showPreview && draft && (
        <TemplatePreviewDialog
          draft={draft}
          sampleLead={sampleLead}
          canSend={canSendTest}
          campaignId={campaignId}
          onClose={() => setShowPreview(false)}
        />
      )}
    </>
  )

  // ---- editing: the Templates-tab split editor, with the wizard's own bar ----
  if (editing && draft) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <SectionTitle hint="The same editor as the Templates tab — source or rich text left, live render right.">
          What they receive
        </SectionTitle>

        <div
          style={{
            height: PANEL_HEIGHT,
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid var(--border-default)',
            borderRadius: 10,
            overflow: 'hidden',
            background: 'var(--surface-box)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              padding: '8px 12px',
              flex: 'none',
              background: 'var(--surface-box-header)',
              borderBottom: '1px solid var(--border-default)',
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 600 }}>Edit template</span>
            <div style={{ flex: 1 }} />
            <TemplateModeToggle mode={editor.mode} onSwitch={editor.switchMode} />
            <Button
              size="sm"
              variant="ghost"
              disabled={editor.saving}
              onClick={() => {
                editor.revert()
                setEditing(false)
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="secondary"
              style={{ color: 'var(--accent-text)' }}
              onClick={() => setShowPreview(true)}
            >
              Show preview
            </Button>
            <Button size="sm" disabled={editor.saving || !editor.dirty} onClick={save}>
              {editor.saving ? 'Saving…' : 'Save'}
            </Button>
          </div>

          <TemplateSplitEditor
            draft={draft}
            mergeFields={mergeFields}
            sampleLead={sampleLead}
            leads={leadRows}
            narrow={narrow}
            onSubject={editor.setSubject}
            onBody={editor.setBody}
            style={{ flex: 1, height: 'auto' }}
          />
        </div>

        {dialogs}
      </div>
    )
  }

  // ---- browsing: picker rail + the same colourful viewer ---------------------
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <SectionTitle hint="Pick a saved template, write one, or paste HTML. Whichever you choose is bound to this campaign and saved to your library.">
        What they receive
      </SectionTitle>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ flex: 1 }} />
        <Button
          size="sm"
          variant="secondary"
          disabled={busy}
          icon={<Ic.template size={14} />}
          onClick={() => setChoosing(true)}
        >
          Choose from templates
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={busy}
          icon={<Ic.upload size={14} />}
          onClick={() => setImporting(true)}
        >
          Paste HTML
        </Button>
        <Button size="sm" disabled={busy} icon={<Ic.plus size={14} />} onClick={writeOne}>
          Write one
        </Button>
      </div>

      {!!selected && !boundTemplateId && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '7px 10px',
            borderRadius: 6,
            background: 'var(--status-pending-bg)',
            color: 'var(--status-pending-fg)',
            fontSize: 12.5,
          }}
        >
          <Ic.warning size={14} />
          Nothing is bound to this campaign yet — press <strong>Use this one</strong> so it can
          actually send.
        </div>
      )}

      <QueryState
        loading={loading}
        error={error}
        empty={!selected}
        emptyTitle="No templates yet"
        emptyDescription="Write one in the editor or paste HTML you already have. Either way it joins your library, so you can reuse it on the next campaign."
        emptyAction={
          <Button icon={<Ic.plus size={14} />} disabled={busy} onClick={writeOne}>
            Write one
          </Button>
        }
        onRetry={onRetry}
      >
        <div
          style={{
            height: narrow ? undefined : PANEL_HEIGHT,
            minHeight: 0,
            display: 'flex',
            flexDirection: narrow ? 'column' : 'row',
            gap: 14,
            alignItems: 'stretch',
          }}
        >
          {/* The rail shows this campaign's own drafts; the whole library is one
              click away behind "Choose from templates". */}
          {ownTemplates.length > 1 && (
            <TemplateList
              templates={ownTemplates}
              activeId={selected?.id ?? null}
              boundId={boundTemplateId}
              onSelect={(t) => open(t, false)}
              narrow={narrow}
            />
          )}

          <TemplateViewer
            rendered={rendered}
            sampleEmail={sampleLead.email}
            fromName={sender.fromName}
            replyTo={sender.replyTo}
            style={narrow ? { height: PANEL_HEIGHT } : undefined}
            actions={
              <>
                {selected && selected.id !== boundTemplateId && (
                  <Button size="sm" disabled={busy} onClick={() => use(selected)}>
                    Use this one
                  </Button>
                )}
                <Button
                  size="sm"
                  icon={<Ic.pencil size={14} />}
                  disabled={!draft}
                  onClick={() => setEditing(true)}
                >
                  Edit template
                </Button>
              </>
            }
          />
        </div>
      </QueryState>

      {dialogs}
    </div>
  )
}

// --- step 4: review -----------------------------------------------------------

export function ReviewStep({
  draft,
  leadCount,
  leadPeek,
  template,
  mode,
  setMode,
  startDate,
  setStartDate,
  startTime,
  setStartTime,
  tz,
  setTz,
  windowOn,
  setWindowOn,
  onEdit,
}: {
  draft: CampaignDraft
  leadCount: number
  leadPeek: Lead[]
  template: Template | null
  mode: 'now' | 'schedule'
  setMode: (m: 'now' | 'schedule') => void
  startDate: string
  setStartDate: (v: string) => void
  startTime: string
  setStartTime: (v: string) => void
  tz: string
  setTz: (v: string) => void
  windowOn: boolean
  setWindowOn: (v: boolean) => void
  onEdit: (s: 'details' | 'leads' | 'template') => void
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 760 }}>
      <SectionTitle hint="Last check before anything leaves.">Review &amp; send</SectionTitle>

      <SummaryCard title="Details" onEdit={() => onEdit('details')}>
        <Row k="Name" v={draft.name} />
        <Row k="Delay" v={describeDelay(draft)} />
        <Row k="Pacing" v={`${draft.daily_cap}/day · ${draft.throttle_seconds}s apart`} />
        {draft.from_name && <Row k="From name" v={draft.from_name} />}
        {draft.reply_to && <Row k="Reply-to" v={draft.reply_to} />}
      </SummaryCard>

      <SummaryCard
        title={`Leads · ${leadCount.toLocaleString()}`}
        onEdit={() => onEdit('leads')}
        editLabel="Import more"
      >
        {leadCount === 0 ? (
          <div style={{ fontSize: 12.5, color: 'var(--status-pending-fg)' }}>
            No leads yet — nothing will send until you add some.
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {leadPeek.map((l) => (
              <span
                key={l.id}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  padding: '2px 8px 2px 2px',
                  borderRadius: 999,
                  background: 'var(--surface-sunken)',
                }}
              >
                <Avatar name={l.name || l.email} size={18} />
                {l.name || l.email}
              </span>
            ))}
            {leadCount > leadPeek.length && (
              <span style={{ fontSize: 12, color: 'var(--text-muted)', alignSelf: 'center' }}>
                +{leadCount - leadPeek.length} more
              </span>
            )}
          </div>
        )}
      </SummaryCard>

      <SummaryCard title="Template" onEdit={() => onEdit('template')} editLabel="Change">
        {template ? (
          <>
            <Row k="Name" v={template.name?.trim() || '(unnamed)'} />
            <Row k="Subject" v={template.subject} />
            <Row k="Format" v={template.body_mode === 'html' ? 'Pasted HTML' : 'Rich text'} />
          </>
        ) : (
          <div style={{ fontSize: 12.5, color: 'var(--danger-text)' }}>No template bound.</div>
        )}
      </SummaryCard>

      {/* when should this send? */}
      <div
        style={{
          border: '1px solid var(--border-default)',
          borderRadius: 6,
          padding: 12,
          background: 'var(--surface-box)',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <div style={{ fontSize: 13.5, fontWeight: 600 }}>When should this send?</div>

        <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          Each lead is scheduled as it arrives — {describeDelay(draft).toLowerCase()}. You can pause
          or cancel any individual send from the Queue.
        </div>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <Radio checked={mode === 'now'} onChange={() => setMode('now')} label="Send now" />
          <Radio checked={mode === 'schedule'} onChange={() => setMode('schedule')} label="Schedule" />
        </div>

        {mode === 'schedule' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.2fr', gap: 10 }}>
              <Field label="Start date">
                <Input type="date" value={startDate} onChange={setStartDate} />
              </Field>
              <Field label="Start time">
                <Input type="time" value={startTime} onChange={setStartTime} />
              </Field>
              <Field label="Timezone">
                <Input value={tz} onChange={setTz} placeholder="Europe/London" />
              </Field>
            </div>
          </div>
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            borderTop: '1px solid var(--border-row)',
            paddingTop: 10,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>Only send within 9am–5pm</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
              Holds sends outside business hours in {tz || 'UTC'}
            </div>
          </div>
          <div style={{ flex: 1 }} />
          <Switch checked={windowOn} onChange={setWindowOn} />
        </div>
      </div>
    </div>
  )
}

function SummaryCard({
  title,
  onEdit,
  editLabel = 'Edit',
  children,
}: {
  title: string
  onEdit: () => void
  editLabel?: string
  children: React.ReactNode
}) {
  return (
    <div
      style={{
        border: '1px solid var(--border-default)',
        borderRadius: 6,
        overflow: 'hidden',
        background: 'var(--surface-box)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '7px 12px',
          background: 'var(--surface-box-header)',
          borderBottom: '1px solid var(--border-default)',
        }}
      >
        <span style={{ fontSize: 12.5, fontWeight: 600 }}>{title}</span>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={onEdit}
          style={{
            border: 'none',
            background: 'transparent',
            padding: 0,
            cursor: 'pointer',
            fontFamily: 'var(--font-sans)',
            fontSize: 12,
            color: 'var(--accent-text)',
          }}
        >
          {editLabel}
        </button>
      </div>
      <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
        {children}
      </div>
    </div>
  )
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ display: 'flex', gap: 10, fontSize: 12.5 }}>
      <span style={{ width: 90, flex: 'none', color: 'var(--text-muted)' }}>{k}</span>
      <span
        style={{
          color: 'var(--text-primary)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {v}
      </span>
    </div>
  )
}
